import { useState, useCallback } from "react";
import { useMap } from "@vis.gl/react-google-maps";

interface MapSnapshot {
  center: google.maps.LatLngLiteral;
  zoom: number;
  mapTypeId: string;
}

interface CourtPlacementState {
  active: boolean;
  locationId: string | null;
  savedMapState: MapSnapshot | null;
  armedSportType: string | null;
}

export function useCourtPlacementMode() {
  const map = useMap();
  const [state, setState] = useState<CourtPlacementState>({
    active: false,
    locationId: null,
    savedMapState: null,
    armedSportType: null,
  });

  const enter = useCallback(
    (locationId: string, center: { lat: number; lng: number }, sidebarOffset?: number) => {
      if (!map) return;

      // Save current map state
      const currentCenter = map.getCenter();
      const savedMapState: MapSnapshot = {
        center: currentCenter
          ? { lat: currentCenter.lat(), lng: currentCenter.lng() }
          : center,
        zoom: map.getZoom() ?? 7,
        mapTypeId: (map.getMapTypeId() as string) ?? "roadmap",
      };

      // Switch to hybrid view (satellite + labels) zoomed in on the location
      map.setMapTypeId("hybrid");
      map.setZoom(20);
      map.panTo(center);

      // Offset the center to account for the sidebar covering the right side
      if (sidebarOffset) {
        map.panBy(sidebarOffset / 2, 0);
      }

      setState({
        active: true,
        locationId,
        savedMapState,
        armedSportType: "soccer_pitch",
      });
    },
    [map],
  );

  const exit = useCallback(() => {
    if (!map || !state.savedMapState) {
      setState({ active: false, locationId: null, savedMapState: null, armedSportType: null });
      return;
    }

    // Restore saved map state
    map.setMapTypeId(state.savedMapState.mapTypeId);
    map.setZoom(state.savedMapState.zoom);
    map.panTo(state.savedMapState.center);

    setState({ active: false, locationId: null, savedMapState: null, armedSportType: null });
  }, [map, state.savedMapState]);

  const arm = useCallback((sportType: string) => {
    setState((prev) => ({ ...prev, armedSportType: sportType }));
  }, []);

  const disarm = useCallback(() => {
    setState((prev) => ({ ...prev, armedSportType: null }));
  }, []);

  return {
    active: state.active,
    locationId: state.locationId,
    savedMapState: state.savedMapState,
    armedSportType: state.armedSportType,
    enter,
    exit,
    arm,
    disarm,
  };
}
